import React from 'react';
import type { SchemaColumn } from '../../types';
import { RegexPill } from '../pills/Pills';

/**
 * Proposed Column Schema table.
 *
 * Default (Profiling Agent): 4 columns —
 *   Column · Type(+pattern) · Validation Pattern · Notes
 *
 * With `profileStats` (Data Content Profiler): the SAME table extended
 * with the profile statistics that already ride on each column as
 * `_raw` (null_rate / uniqueness_ratio / sample_values) →
 *   Column · Type(+pattern) · Validation · Null % · Unique % · Samples · Notes
 *
 * One component, both agents — no duplicate table. `profileStats`
 * defaults false so the Profiling Agent renders byte-identically.
 */

interface SchemaProposalTableProps {
  columns: SchemaColumn[];
  className?: string;
  profileStats?: boolean;
}

const pct = (r?: number) =>
  typeof r === 'number' ? `${(r * 100).toFixed(1)}%` : '—';


export const SchemaProposalTable: React.FC<SchemaProposalTableProps> = ({
  columns, className = '', profileStats = false,
}) => (
  <div className={`overflow-hidden rounded-lg border border-cargo-border ${className}`}>
    <table className="w-full table-fixed text-[13px] leading-5">
      <thead className="bg-cargo-panel text-cargo-muted text-[11px] uppercase tracking-wider font-medium">
        <tr>
          <th className={`text-left px-4 py-3 align-bottom ${profileStats ? 'w-[14%]' : 'w-[16%]'}`}>Column<br />Name</th>
          <th className={`text-left px-4 py-3 align-bottom ${profileStats ? 'w-[20%]' : 'w-[26%]'}`}>Type</th>
          <th className={`text-left px-4 py-3 align-bottom ${profileStats ? 'w-[16%]' : 'w-[28%]'}`}>
            {profileStats ? <>Validation<br />Pattern</> : 'Validation Pattern'}
          </th>
          {profileStats && (
            <>
              <th className="text-right px-4 py-3 align-bottom w-[8%]">Null %</th>
              <th className="text-right px-4 py-3 align-bottom w-[8%]">Unique %</th>
              <th className="text-left px-4 py-3 align-bottom w-[18%]">Samples</th>
            </>
          )}
          <th className={`text-left px-4 py-3 align-bottom ${profileStats ? 'w-[16%]' : 'w-[30%]'}`}>Notes</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-cargo-border">
        {columns.map((col) => (
          <SchemaRow key={col.name} column={col} profileStats={profileStats} />
        ))}
      </tbody>
    </table>
  </div>
);

const SchemaRow: React.FC<{ column: SchemaColumn; profileStats: boolean }> = ({
  column, profileStats,
}) => {
  const raw: any = (column as any)._raw || {};
  // Profiling Agent (no profileStats): EXACT original padding/leading —
  // byte-identical. Data Content Profiler: roomier rows + relaxed
  // line-height so it reads neat and uncramped.
  const pad = profileStats ? 'px-4 py-5' : 'px-4 py-3.5';
  const lead = profileStats ? ' leading-relaxed' : '';
  const samples: any[] = Array.isArray(raw.sample_values)
    ? raw.sample_values.slice(0, 4)
    : [];
  return (
    <tr className="hover:bg-cargo-panel transition-colors">
      <td className={`${pad} text-cargo-text font-medium align-top break-words${lead}`}>
        {column.name}
      </td>
      <td className={`${pad} text-cargo-text align-top`}>
        <span className="inline-flex flex-wrap items-baseline gap-1.5">
          <span className="text-cargo-muted">{column.type_base}</span>
          {column.regex_pattern && (
            <>
              <span className="text-cargo-muted">(pattern:</span>
              <RegexPill value={column.regex_pattern} />
              <span className="text-cargo-muted">)</span>
            </>
          )}
        </span>
      </td>
      <td className={`${pad} text-cargo-muted align-top break-words${lead}`}>
        {column.validation_meaning}
      </td>
      {profileStats && (
        <>
          <td className={`${pad} text-cargo-muted align-top text-right`}>
            {pct(raw.null_rate)}
          </td>
          <td className={`${pad} text-cargo-muted align-top text-right`}>
            {pct(raw.uniqueness_ratio)}
          </td>
          <td className={`${pad} text-cargo-muted align-top font-mono text-[11px] break-words`}>
            {samples.length
              ? (
                <div className="space-y-1.5">
                  {samples.map((v, i) => (
                    <div key={i} className="break-all">{String(v)}</div>
                  ))}
                </div>
              )
              : '—'}
          </td>
        </>
      )}
      <td className={`${pad} text-cargo-muted align-top break-words${lead}`}>
        {column.notes}
      </td>
    </tr>
  );
};
